/**
 * TrendRegressionChart — per-well scatter + linear regression trend lines
 * with 12-month projection. Native hand-rolled SVG (no Recharts).
 */

import { useState, useMemo, useRef, useEffect, useCallback, useId } from 'react';
import { scaleTime, scaleLinear } from 'd3-scale';
import type { GaugingTimeseriesResponse } from '@/api/dataVault';
import { linearRegression } from './linearRegression';
import { WellSelectorPanel } from './WellSelectorPanel';
import { WELL_COLORS } from './chartTheme';
import { ChartTooltip } from './ChartTooltip';
import { useChartView } from './interactions/useChartView';
import { ChartTimeRangeRow } from './ChartTimeRangeRow';
import { defaultHiddenWellIds } from './chartDefaults';

type Metric = 'depth_to_water_ft' | 'water_elevation_ft';

const PROJECTION_MONTHS = 12;
const MS_PER_MONTH = 30.44 * 24 * 60 * 60 * 1000;
const MARGIN = { top: 12, right: 24, bottom: 40, left: 64 };
const CHART_HEIGHT = 420;

interface ScatterPoint {
  ts: number;
  value: number;
  date: string;
  well: string;
  color: string;
}

interface Props {
  data: GaugingTimeseriesResponse;
}

export function TrendRegressionChart({ data }: Props) {
  const [metric, setMetric] = useState<Metric>('depth_to_water_ft');
  // Regression overlays per-well scatter + trend lines — default to all wells.
  const [hiddenWellIds, setHiddenWellIds] = useState<Set<string>>(new Set());
  const [showDots, setShowDots] = useState(true);
  const [showRegression, setShowRegression] = useState(true);

  const visibleWells = data.wells.filter((w) => !hiddenWellIds.has(w.well_id));

  const { scatterByWell, regressionLineByWell, maxTs } = useMemo(() => {
    const scatter: Record<string, ScatterPoint[]> = {};
    const regLines: Record<string, Array<{ ts: number; value: number }>> = {};
    let maxT = -Infinity;

    for (const well of visibleWells) {
      const wellIdx = data.wells.findIndex((w) => w.well_id === well.well_id);
      const color = WELL_COLORS[wellIdx % WELL_COLORS.length];
      const pts: ScatterPoint[] = [];
      const regPts: Array<[number, number]> = [];
      for (const m of well.measurements) {
        const val = m[metric];
        if (val == null) continue;
        const ts = new Date(m.date).getTime();
        pts.push({ ts, value: val, date: m.date, well: well.well_name, color });
        regPts.push([ts, val]);
        if (ts > maxT) maxT = ts;
      }
      scatter[well.well_id] = pts;
      if (showRegression && regPts.length >= 2) {
        const reg = linearRegression(regPts);
        if (reg) {
          const startT = Math.min(...regPts.map((p) => p[0]));
          const projT = Math.max(...regPts.map((p) => p[0])) + PROJECTION_MONTHS * MS_PER_MONTH;
          regLines[well.well_id] = [
            { ts: startT, value: reg.predict(startT) },
            { ts: projT, value: reg.predict(projT) },
          ];
          if (projT > maxT) maxT = projT;
        }
      }
    }
    return { scatterByWell: scatter, regressionLineByWell: regLines, maxTs: maxT };
  }, [visibleWells, metric, showRegression, data.wells]);

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(800);
  const clipId = `reg-clip-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, CHART_HEIGHT - MARGIN.top - MARGIN.bottom);

  const baseXDomain = useMemo<readonly [number, number]>(() => {
    let lo = Infinity, hi = maxTs;
    for (const pts of Object.values(scatterByWell)) {
      for (const p of pts) if (p.ts < lo) lo = p.ts;
    }
    if (!isFinite(lo) || !isFinite(hi)) return [0, 1];
    if (lo === hi) return [lo - 86_400_000, hi + 86_400_000];
    return [lo, hi];
  }, [scatterByWell, maxTs]);

  const baseYDomain = useMemo<readonly [number, number]>(() => {
    let lo = Infinity, hi = -Infinity;
    for (const pts of Object.values(scatterByWell)) {
      for (const p of pts) {
        if (p.value < lo) lo = p.value;
        if (p.value > hi) hi = p.value;
      }
    }
    if (!isFinite(lo) || !isFinite(hi)) return [0, 1];
    const pad = Math.max((hi - lo) * 0.05, 0.5);
    return [lo - pad, hi + pad];
  }, [scatterByWell]);

  const xPxToDataRef = useRef<(px: number) => number>(() => 0);
  const yPxToDataRef = useRef<(py: number) => number>(() => 0);
  const view = useChartView(
    svgRef,
    (px) => xPxToDataRef.current(px),
    (py) => yPxToDataRef.current(py),
    {
      baseXDomain,
      baseYDomain,
      dragZoomAxes: 'x',
      chartArea: {
        top: MARGIN.top,
        bottom: MARGIN.top + innerHeight,
        left: MARGIN.left,
        right: MARGIN.left + innerWidth,
      },
    },
  );

  // For DTW, reverse the visual y direction (deeper = lower on screen).
  const yReversed = metric === 'depth_to_water_ft';

  const xScale = useMemo(
    () => scaleTime().domain([view.xDomain[0], view.xDomain[1]]).range([MARGIN.left, MARGIN.left + innerWidth]),
    [view.xDomain, innerWidth],
  );
  const yScale = useMemo(
    () =>
      scaleLinear()
        .domain([view.yDomain[0], view.yDomain[1]])
        .range(yReversed ? [MARGIN.top, MARGIN.top + innerHeight] : [MARGIN.top + innerHeight, MARGIN.top])
        .nice(),
    [view.yDomain, innerHeight, yReversed],
  );
  xPxToDataRef.current = (px: number) => xScale.invert(px).getTime();
  yPxToDataRef.current = (py: number) => yScale.invert(py);

  const xTicks = useMemo(() => xScale.ticks(6), [xScale]);
  const yTicks = useMemo(() => yScale.ticks(6), [yScale]);

  const formatX = (d: Date) => `${d.getMonth() + 1}/${d.getFullYear().toString().slice(2)}`;
  const formatY = (v: number) => v.toFixed(1);

  interface HoverState { point: ScatterPoint; clientX: number; clientY: number }
  const [hover, setHover] = useState<HoverState | null>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const svg = svgRef.current; if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const lx = e.clientX - rect.left;
      const ly = e.clientY - rect.top;
      let best: ScatterPoint | null = null;
      let bestDsq = 14 * 14;
      for (const pts of Object.values(scatterByWell)) {
        for (const p of pts) {
          const px = xScale(p.ts);
          const py = yScale(p.value);
          const dx = px - lx;
          const dy = py - ly;
          const dsq = dx * dx + dy * dy;
          if (dsq < bestDsq) {
            bestDsq = dsq;
            best = p;
          }
        }
      }
      if (best) setHover({ point: best, clientX: e.clientX, clientY: e.clientY });
      else setHover(null);
    },
    [scatterByWell, xScale, yScale],
  );
  const handleMouseLeave = useCallback(() => setHover(null), []);

  if (!data.wells.length) {
    return (
      <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
        NO TIME-SERIES DATA AVAILABLE
      </div>
    );
  }

  const metricLabel = metric === 'depth_to_water_ft' ? 'Depth to Water (ft)' : 'Water Elevation (ft)';
  const wellItems = data.wells.map((w, i) => ({
    well_id: w.well_id,
    well_name: w.well_name,
    color: WELL_COLORS[i % WELL_COLORS.length],
  }));
  const visibleWellIdSet = new Set(visibleWells.map((w) => w.well_id));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-mono-500 tracking-wider">METRIC:</span>
          {[
            { key: 'depth_to_water_ft' as Metric, label: 'DTW' },
            { key: 'water_elevation_ft' as Metric, label: 'ELEVATION' },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setMetric(opt.key)}
              className={`px-2 py-1 text-[10px] tracking-wider rounded transition-colors ${
                metric === opt.key
                  ? 'bg-mono-700 text-mono-100'
                  : 'text-mono-500 hover:text-mono-300 hover:bg-mono-800'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDots(!showDots)}
            className={`px-2 py-1 text-[10px] tracking-wider rounded transition-colors ${
              showDots ? 'bg-cyan-900/50 text-cyan-300 border border-cyan-700/50' : 'text-mono-500 hover:text-mono-300 hover:bg-mono-800'
            }`}
          >
            SCATTER
          </button>
          <button
            onClick={() => setShowRegression(!showRegression)}
            className={`px-2 py-1 text-[10px] tracking-wider rounded transition-colors ${
              showRegression ? 'bg-cyan-900/50 text-cyan-300 border border-cyan-700/50' : 'text-mono-500 hover:text-mono-300 hover:bg-mono-800'
            }`}
          >
            TREND LINES
          </button>
          <span className="text-[10px] text-mono-500 tracking-wider">
            {visibleWells.length}/{data.wells.length} WELLS
          </span>
        </div>
      </div>

      <WellSelectorPanel
        wells={wellItems}
        visibleWellIds={visibleWellIdSet}
        onToggle={(id) => {
          setHiddenWellIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          });
        }}
        onSelectAll={() => setHiddenWellIds(new Set())}
        onDeselectAll={() => setHiddenWellIds(new Set(data.wells.map((w) => w.well_id)))}
      />

      <ChartTimeRangeRow
        dataMinMs={baseXDomain[0]}
        dataMaxMs={baseXDomain[1]}
        activeDomain={view.xDomain}
        onSelect={(d) => view.setXDomain(d)}
        isZoomed={view.isZoomed}
        onReset={view.reset}
      />

      <div ref={containerRef}>
        {visibleWells.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
            SELECT WELLS TO DISPLAY
          </div>
        ) : (
          <svg
            ref={svgRef}
            width={width || '100%'}
            height={CHART_HEIGHT}
            style={{ display: 'block', userSelect: 'none', touchAction: 'none', cursor: 'crosshair' }}
          >
            <defs>
              <clipPath id={clipId}>
                <rect x={MARGIN.left} y={MARGIN.top} width={innerWidth} height={innerHeight} />
              </clipPath>
            </defs>

            <g clipPath={`url(#${clipId})`}>
              {/* Gridlines */}
              {yTicks.map((t) => {
                const y = yScale(t);
                return (
                  <line
                    key={`grid-${t}`}
                    x1={MARGIN.left}
                    x2={MARGIN.left + innerWidth}
                    y1={y}
                    y2={y}
                    stroke="var(--dv-chart-grid)"
                    strokeDasharray="3 3"
                    strokeWidth={1}
                  />
                );
              })}

              {/* Regression lines per well */}
              {showRegression &&
                visibleWells.map((well) => {
                  const seg = regressionLineByWell[well.well_id];
                  if (!seg || seg.length < 2) return null;
                  const wellIdx = data.wells.findIndex((w) => w.well_id === well.well_id);
                  const color = WELL_COLORS[wellIdx % WELL_COLORS.length];
                  return (
                    <line
                      key={`reg-${well.well_id}`}
                      x1={xScale(seg[0].ts)}
                      y1={yScale(seg[0].value)}
                      x2={xScale(seg[1].ts)}
                      y2={yScale(seg[1].value)}
                      stroke={color}
                      strokeWidth={2}
                      strokeDasharray="6 3"
                    />
                  );
                })}

              {/* Scatter dots per well */}
              {showDots &&
                visibleWells.map((well) =>
                  (scatterByWell[well.well_id] ?? []).map((p, i) => {
                    const isHovered = hover?.point === p;
                    return (
                      <circle
                        key={`pt-${well.well_id}-${i}`}
                        cx={xScale(p.ts)}
                        cy={yScale(p.value)}
                        r={isHovered ? 4.5 : 3}
                        fill={p.color}
                        opacity={0.85}
                        stroke="var(--dv-bg-card)"
                        strokeWidth={isHovered ? 2 : 1}
                        style={isHovered ? { filter: 'url(#dv-hover-halo)' } : undefined}
                      />
                    );
                  }),
                )}
            </g>

            {/* Y-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {yTicks.map((t) => {
              const y = yScale(t);
              return (
                <g key={`yt-${t}`}>
                  <line x1={MARGIN.left - 4} x2={MARGIN.left} y1={y} y2={y} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={MARGIN.left - 8} y={y} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatY(t)}
                  </text>
                </g>
              );
            })}
            <text transform={`translate(${MARGIN.left - 48}, ${MARGIN.top + innerHeight / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
              {metricLabel}
            </text>

            {/* X-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {xTicks.map((t, i) => {
              const x = xScale(t);
              return (
                <g key={`xt-${i}`}>
                  <line x1={x} x2={x} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight + 4} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={x} y={MARGIN.top + innerHeight + 16} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatX(t)}
                  </text>
                </g>
              );
            })}

            {view.rectPreview && (
              <rect
                x={view.rectPreview.x0}
                y={view.rectPreview.y0}
                width={view.rectPreview.x1 - view.rectPreview.x0}
                height={view.rectPreview.y1 - view.rectPreview.y0}
                fill="rgba(16, 185, 129, 0.12)"
                stroke="var(--dv-accent-emerald, #10b981)"
                strokeWidth={1}
                strokeDasharray="3 3"
                pointerEvents="none"
              />
            )}

            <rect
              x={MARGIN.left}
              y={MARGIN.top}
              width={innerWidth}
              height={innerHeight}
              fill="transparent"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            />
          </svg>
        )}
      </div>

      {hover && (
        <ChartTooltip visible x={hover.clientX} y={hover.clientY} placement="top">
          <div style={{ minWidth: 180, fontSize: 11 }}>
            <div style={{ fontSize: 10, color: 'var(--dv-tooltip-label, rgba(255,255,255,0.7))', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              {hover.point.date}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 999, background: hover.point.color }} />
              <span style={{ flex: 1 }}>{hover.point.well}</span>
              <span style={{ fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)', fontWeight: 600 }}>
                {hover.point.value.toFixed(2)} ft
              </span>
            </div>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}
